import {
  UserCircle as AccountCircleIcon,
  ChevronUp as ExpandLessIcon,
  ChevronDown as ExpandMoreIcon,
  Menu as MenuIcon,
  Star as RatingIcon,
  Eye as WatchListIcon,
} from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { logout } from './api/movies';
import { IconButton } from './components/Button';
import { Drawer } from './components/Drawer';
import { FadeInImage } from './components/FadeInImage';
import { Flex } from './components/Flex';
import { PickMovieModal } from './components/PickMovieModal';
import { Popover } from './components/Popover';
import { UserOptionsModal } from './components/UserOptionsModal';
import { useAllUsers } from './hooks/useMovieData';
import { SearchField } from './SearchField';
import { UserContext } from './UserContext';

export function NavBar({}) {
  const user_data = useContext(UserContext);
  const { query } = useParams();

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [itemExpanded, setItemExpanded] = useState({});
  const [userOptionsModalOpen, setUserOptionsModalOpen] = useState(false);
  const [pickModalOpen, setPickModalOpen] = useState(false);
  const { users = [], isLoading } = useAllUsers();

  const handleLogout = React.useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      localStorage.removeItem('user');
      window.location.assign('/');
    }
  }, []);

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Releases', path: '/upcoming' },
    {
      label: 'My ratings',
      path: `/u/${user_data.username}/ratings`,
      drawerIcon: <RatingIcon size={16} />,
    },
    {
      label: 'My watchlist',
      path: `/u/${user_data.username}/watchlist`,
      drawerIcon: <WatchListIcon size={16} />,
    },
    {
      label: 'Users',
      items: users.map((user) => ({
        label: user.display_name || user.username,
        options: [
          {
            icon: <RatingIcon size={22} />,
            path: `/u/${user.username}/ratings`,
          },
          {
            icon: <WatchListIcon size={22} />,
            path: `/u/${user.username}/watchlist`,
          },
        ],
      })),
    },
    {
      label: user_data.display_name ?? user_data.username,
      drawerIcon: <AccountCircleIcon size={16} />,
      menuIcon: <AccountCircleIcon size={16} />,
      items: [
        {
          label: 'Edit profile',
          action: () => {
            setUserOptionsModalOpen(true);
            setItemExpanded({});
          },
        },
        {
          label: 'Pick a movie for me',
          action: () => {
            setPickModalOpen(true);
            setItemExpanded({});
          },
        },
        { label: 'Logout', action: handleLogout },
      ],
    },
  ];

  function handleSearch(e) {
    if (e.keyCode == 13) {
      navigate(`/search/${searchQuery}`);
    }
  }

  useEffect(() => {
    setSearchQuery(query ?? '');
  }, [query]);

  function renderMenu(menuItems, renderAsButton) {
    if (!menuItems) {
      return null;
    }
    return menuItems.map((item) => {
      if (renderAsButton) {
        const menuOpen = itemExpanded[item.label + 'menu'] == true;

        return (
          <div key={item.label} className="navbar__menu-item">
            {item.path ? (
              <Link
                to={item.path}
                className="btn btn--secondary navbar__menu-button"
                onClick={(e) => {
                  if (item.items?.length > 0) {
                    e.preventDefault();
                    handleExpandItem(e, item.label, menuOpen);
                  }
                }}
              >
                <Flex direction="row" align="center" gap="0.35rem">
                  {item.menuIcon}
                  {item.label}
                </Flex>
              </Link>
            ) : (
              <div
                className="btn btn--secondary navbar__menu-button"
                onClick={(e) => {
                  if (item.items?.length > 0) {
                    handleExpandItem(e, item.label, menuOpen);
                  }
                }}
              >
                <Flex direction="row" align="center" gap="0.35rem">
                  {item.menuIcon}
                  {item.label}
                </Flex>
              </div>
            )}

            {item.items?.length > 0 && (
              <Popover
                anchorEl={menuAnchorElement}
                open={menuOpen}
                onClose={() => handleExpandItem(null, item.label, true)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Flex direction="column" className="navbar__menu-popover">
                  {item.items.map((subItem) => (
                    <Flex
                      key={subItem.label}
                      direction="row"
                      align="center"
                      className="users"
                    >
                      <span
                        className={`users__username${subItem.action ? ' users__username--action' : ''}`}
                        onClick={() => {
                          if (subItem.action) {
                            subItem.action();
                          }
                        }}
                      >
                        {subItem.label}
                      </span>
                      {renderOptions(subItem, item)}
                    </Flex>
                  ))}
                </Flex>
              </Popover>
            )}
          </div>
        );
      }

      const drawerOpen = itemExpanded[item.label + 'drawer'] == true;
      return (
        <div key={item.label} className="drawer__item">
          {item.path ? (
            <Link
              to={item.path}
              className="drawer__button"
              onClick={(e) => {
                if (item.items?.length > 0) {
                  e.preventDefault();
                  handleExpandItem(e, item.label, drawerOpen, 'drawer');
                } else {
                  setDrawerOpened(false);
                }
              }}
            >
              <Flex direction="row" align="center" gap="0.5rem">
                {item.drawerIcon}
                <span>{item.label}</span>
              </Flex>
              {item.items?.length > 0 &&
                (drawerOpen ? (
                  <ExpandLessIcon size={16} />
                ) : (
                  <ExpandMoreIcon size={16} />
                ))}
              {renderOptions(item)}
            </Link>
          ) : item.action ? (
            <button
              className="drawer__button"
              onClick={() => {
                item.action();
                setDrawerOpened(false);
              }}
            >
              <Flex direction="row" align="center" gap="0.5rem">
                {item.drawerIcon}
                <span>{item.label}</span>
              </Flex>
            </button>
          ) : (
            <button
              className="drawer__button"
              onClick={(e) =>
                handleExpandItem(e, item.label, drawerOpen, 'drawer')
              }
            >
              <Flex
                direction="row"
                align="center"
                gap="0.5rem"
                style={{ flexGrow: 1 }}
              >
                {item.drawerIcon}
                <span>{item.label}</span>
              </Flex>
              {item.items?.length > 0 &&
                (drawerOpen ? (
                  <ExpandLessIcon size={16} />
                ) : (
                  <ExpandMoreIcon size={16} />
                ))}
              {renderOptions(item)}
            </button>
          )}

          {item.items?.length > 0 && (
            <div className={`drawer__submenu ${drawerOpen ? 'is-open' : ''}`}>
              {renderMenu(item.items, renderAsButton)}
            </div>
          )}
        </div>
      );
    });
  }

  function renderOptions(subItem, parentItem = null) {
    return subItem.options?.map((option) => (
      <Link
        to={option.path}
        key={option.path}
        aria-label={option.label}
        title={option.label}
        className="option-icon"
        onClick={(e) => {
          if (parentItem) {
            handleExpandItem(
              e,
              parentItem.label,
              itemExpanded[parentItem.label + 'menu'],
            );
          }
          setDrawerOpened(false);
        }}
      >
        {option.icon}
      </Link>
    ));
  }

  const handleExpandItem = (e, id, open, type = 'menu') => {
    if (type === 'menu' && e?.currentTarget) {
      setMenuAnchorElement(e.currentTarget);
    }
    const key = id + type;
    setItemExpanded({ ...itemExpanded, [key]: !open });
  };

  const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);

  const [drawerOpened, setDrawerOpened] = React.useState(false);
  const toggleDrawer = (open) => setDrawerOpened(open);

  return (
    <header className="navbar">
      <div className="navbar__logo">
        <FadeInImage
          src="/icon.svg"
          alt="RAW2 Logo"
          width={24}
          height={24}
          loading="eager"
          style={{ margin: '.25rem', marginRight: '0.75rem' }}
          onLoad={() => {}}
          onError={() => {}}
        />
      </div>
      <nav className="navbar__menu" aria-label="Main navigation">
        {renderMenu(menuItems, true)}
      </nav>

      <div className="navbar__search">
        <SearchField
          placeholder="Search..."
          value={searchQuery}
          onKeyDown={handleSearch}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <IconButton
        className="navbar__menu-toggle"
        onClick={() => toggleDrawer(true)}
        aria-label="Open navigation menu"
        title="Menu"
      >
        <MenuIcon size={24} />
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpened}
        onClose={() => toggleDrawer(false)}
        width={300}
      >
        <div className="drawer__content">{renderMenu(menuItems, false)}</div>
      </Drawer>

      <UserOptionsModal
        isOpen={userOptionsModalOpen}
        onClose={() => setUserOptionsModalOpen(false)}
        userData={user_data}
        onSuccess={(updatedOptions) => {
          const updated = {
            ...user_data,
            options: updatedOptions,
            display_name: updatedOptions.display_name,
          };
          localStorage.setItem('user', JSON.stringify(updated));
          window.location.reload();
        }}
      />
      <PickMovieModal
        isOpen={pickModalOpen}
        onClose={() => setPickModalOpen(false)}
      />
    </header>
  );
}
